import { GoogleIcon } from '../common/icons/GoogleIcon';
import { YelpIcon } from '../common/icons/YelpIcon';
import { BBBIcon } from '../common/icons/BBBIcon';
import { PUBLIC_SCORECARD_PREVIEW_ID } from './PublicScorecardFormHeader';

type ExampleMetricProps = {
  label: string;
  value: string;
  detail?: string;
  status: 'above' | 'average' | 'below' | 'low';
  trend?: boolean;
};

const METRIC_STYLES: Record<
  ExampleMetricProps['status'],
  { body: string; border: string; label: string; footer: string }
> = {
  above: {
    body: 'bg-emerald-700 text-white',
    border: 'border-emerald-700',
    label: 'Above Average',
    footer: 'text-emerald-700',
  },
  average: {
    body: 'bg-blue-700 text-white',
    border: 'border-blue-700',
    label: 'Average',
    footer: 'text-blue-700',
  },
  below: {
    body: 'bg-amber-300 text-amber-950',
    border: 'border-amber-400',
    label: 'Below Average',
    footer: 'text-amber-900',
  },
  low: {
    body: 'bg-red-700 text-white',
    border: 'border-red-700',
    label: 'Extremely Low',
    footer: 'text-red-700',
  },
};

function ExampleMetric({ label, value, detail, status, trend }: ExampleMetricProps) {
  const styles = METRIC_STYLES[status];

  return (
    <li className={`flex min-h-32 flex-col overflow-hidden rounded-lg border ${styles.border}`}>
      <div
        className={`flex flex-1 flex-col items-center justify-center px-2 py-3 text-center ${styles.body}`}
      >
        <p className="text-xs font-bold">{label}</p>
        <p className="mt-2 text-xl font-extrabold leading-none">{value}</p>
        {detail ? <p className="mt-1.5 text-xs">{detail}</p> : null}
        {trend ? (
          <p
            aria-label="Trending up"
            className="mt-1.5 flex items-center gap-1 text-xs font-bold"
            role="img"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 13 13 3M8 3h5v5" />
            </svg>
            Trending
          </p>
        ) : null}
      </div>
      <div className={`bg-white px-2 py-2 text-center text-xs font-bold ${styles.footer}`}>
        {styles.label}
      </div>
    </li>
  );
}

function SourceMark({ children }: { children: React.ReactNode }) {
  return <span className="h-4 w-4 shrink-0 [&>svg]:h-4 [&>svg]:w-4">{children}</span>;
}

function ExampleAIIcon() {
  return (
    <svg aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 16 16">
      <path d="M8 1.5c.4 3.5 2.1 5.2 5.5 5.5C10.1 7.4 8.4 9.1 8 12.5 7.6 9.1 5.9 7.4 2.5 7 5.9 6.6 7.6 4.9 8 1.5Z" />
      <path d="M12.5 11c.2 1.4.9 2.1 2 2.5-1.1.3-1.8 1-2 2-.3-1-1-1.7-2-2 1-.4 1.7-1.1 2-2.5Z" />
    </svg>
  );
}

type OpportunityIconName = 'reviews' | 'rating' | 'response-rate' | 'response-time';

const OPPORTUNITY_ICON_STYLES: Record<OpportunityIconName, string> = {
  reviews: 'bg-emerald-50 text-emerald-700',
  rating: 'bg-blue-50 text-blue-700',
  'response-rate': 'bg-amber-100 text-amber-900',
  'response-time': 'bg-red-50 text-red-700',
};

function ExampleOpportunityIcon({ name }: { name: OpportunityIconName }) {
  const path =
    name === 'reviews'
      ? 'M3 3h10v7H8l-3 3v-3H3V3Z'
      : name === 'rating'
        ? 'm8 2 1.8 3.6 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4-2.9-2.8 4-.6L8 2Z'
        : name === 'response-rate'
          ? 'M6 4 3 7l3 3M3 7h6a4 4 0 1 1-3.5 6'
          : 'M3 3h10v10H3V3Zm0 3h10M6 1.5v3M10 1.5v3M6 9h1M9 9h1M6 11h1M9 11h1';

  return (
    <span
      aria-hidden="true"
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${OPPORTUNITY_ICON_STYLES[name]}`}
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        viewBox="0 0 16 16"
      >
        <path d={path} />
      </svg>
    </span>
  );
}

function ExampleStars({ rating }: { rating: number }) {
  const roundedRating = Math.round(rating * 2) / 2;

  return (
    <span
      aria-label={`${rating.toFixed(1)} out of 5 stars`}
      className="inline-flex gap-0.5"
      role="img"
    >
      {Array.from({ length: 5 }, (_, index) => {
        const starNumber = index + 1;
        const filled = roundedRating >= starNumber;
        const half = !filled && roundedRating >= starNumber - 0.5;
        const gradientId = `example-star-${String(rating).replace('.', '-')}-${starNumber}`;

        return (
          <svg
            aria-hidden="true"
            className="h-3.5 w-3.5"
            fill={filled ? 'currentColor' : half ? `url(#${gradientId})` : 'none'}
            key={starNumber}
            stroke="currentColor"
            strokeLinejoin="round"
            strokeWidth="1.25"
            viewBox="0 0 16 16"
          >
            {half ? (
              <defs>
                <linearGradient id={gradientId} x1="0" x2="1">
                  <stop offset="50%" stopColor="currentColor" />
                  <stop offset="50%" stopColor="white" />
                </linearGradient>
              </defs>
            ) : null}
            <path d="M8 1.5 10 5.4l4.4.7-3.2 3.1.8 4.4L8 11.5l-4 2.1.8-4.4-3.2-3.1 4.4-.7L8 1.5Z" />
          </svg>
        );
      })}
    </span>
  );
}

export function PublicScorecardPreview() {
  return (
    <section
      data-testid="public-scorecard-preview"
      id={PUBLIC_SCORECARD_PREVIEW_ID}
      aria-label="Example scorecard"
      className="scroll-mt-4 mx-auto w-full max-w-4xl overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm"
    >
      <header className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
        <h2 className="text-base font-semibold text-slate-900">Example scorecard</h2>
        <p className="mt-0.5 text-sm text-slate-600">
          This is sample data. Your scorecard will use your business data.
        </p>
      </header>

      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <div>
            <h3 className="text-xl font-bold text-slate-950">Reputation Scorecard</h3>
            <p className="text-xs text-slate-500">Created today</p>
          </div>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
            Sample
          </span>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="flex min-w-0 flex-col">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center border-[3px] border-amber-400 text-3xl font-extrabold text-slate-950">
                C-
              </div>
              <div className="min-w-0 text-center sm:text-left">
                <h4 className="text-lg font-bold text-slate-950">Harbor Coffee Co.</h4>
                <p className="text-xs text-slate-600">123 Main Street, Austin, TX</p>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 text-xs sm:justify-start">
                  <span>4.2</span>
                  <span className="inline-flex text-slate-950">
                    <ExampleStars rating={4.2} />
                  </span>
                  <span className="font-medium text-blue-700">684 Google reviews</span>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h5 className="text-sm font-bold text-slate-950">Local Competitors</h5>
              <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-2.5 text-xs">
                  <p className="font-bold text-blue-700">Juniper Coffee</p>
                  <p className="text-slate-500">0.8 mi away</p>
                  <div className="mt-1 flex items-center gap-1">
                    <SourceMark>
                      <GoogleIcon />
                    </SourceMark>
                    <span className="font-bold">4.7</span>
                    <ExampleStars rating={4.7} />
                  </div>
                  <p className="mt-1 text-blue-700">1,120 Google reviews</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-2.5 text-xs">
                  <p className="font-bold text-blue-700">South Congress Roasters</p>
                  <p className="text-slate-500">1.3 mi away</p>
                  <div className="mt-1 flex items-center gap-1">
                    <SourceMark>
                      <GoogleIcon />
                    </SourceMark>
                    <span className="font-bold">4.6</span>
                    <ExampleStars rating={4.6} />
                  </div>
                  <p className="mt-1 text-blue-700">853 Google reviews</p>
                </div>
              </div>
            </div>

            <div
              role="group"
              aria-label="Example reporting range"
              className="mt-auto flex flex-wrap items-center gap-1 pt-4 text-xs font-semibold text-slate-700"
            >
              <span className="rounded-full px-2 py-1">30 Days</span>
              <span className="rounded-full px-2 py-1">60 Days</span>
              <span className="rounded-full px-2 py-1">90 Days</span>
              <span className="rounded-full bg-blue-600 px-3 py-1 text-white">180 Days</span>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-3">
            <div className="rounded-xl border border-slate-300 p-3">
              <h5 className="font-bold text-slate-950">Lifetime Stats</h5>
              <div className="mt-2 space-y-1.5 text-xs">
                <div className="flex flex-wrap items-center gap-1">
                  <SourceMark>
                    <GoogleIcon />
                  </SourceMark>
                  <span className="font-bold">4.3</span>
                  <ExampleStars rating={4.3} />
                  <span className="text-blue-700">720 reviews</span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <SourceMark>
                    <YelpIcon />
                  </SourceMark>
                  <span className="font-bold">4.1</span>
                  <ExampleStars rating={4.1} />
                  <span className="text-blue-700">128 reviews</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border-2 border-slate-900">
              <div className="border-b border-slate-200 px-3 py-2.5">
                <h5 className="font-bold text-slate-950">Your Biggest Opportunities</h5>
                <p className="mt-0.5 text-xs leading-4 text-slate-600">
                  Combined performance across all review sites, last 180 days.
                </p>
              </div>
              <div className="divide-y divide-slate-200 px-3 text-xs">
                <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 py-2">
                  <ExampleOpportunityIcon name="reviews" />
                  <div>
                    <p className="font-bold text-slate-950">24 &rarr; 48+ reviews/month</p>
                    <p className="text-slate-600">Generate more Google reviews.</p>
                  </div>
                </div>
                <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 py-2">
                  <ExampleOpportunityIcon name="rating" />
                  <div>
                    <p className="font-bold text-slate-950">4.21 &rarr; 4.5+</p>
                    <p className="text-slate-600">Improve your average rating.</p>
                  </div>
                </div>
                <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 py-2">
                  <ExampleOpportunityIcon name="response-rate" />
                  <div>
                    <p className="font-bold text-slate-950">42% &rarr; 100%</p>
                    <p className="text-slate-600">Respond to every review.</p>
                  </div>
                </div>
                <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 py-2">
                  <ExampleOpportunityIcon name="response-time" />
                  <div>
                    <p className="font-bold text-slate-950">6.2 days &rarr; &lt;1 day</p>
                    <p className="text-slate-600">Respond within one business day.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Example review sources"
          className="mt-4 flex flex-wrap items-center gap-4 border-b border-slate-300 text-xs font-semibold text-slate-700"
        >
          <span
            role="tab"
            aria-selected="true"
            className="flex items-center gap-1.5 border-b-2 border-blue-600 px-1 py-2 text-slate-950"
          >
            <SourceMark>
              <GoogleIcon />
            </SourceMark>{' '}
            Google
          </span>
          <span role="tab" aria-selected="false" className="flex items-center gap-1.5 px-1 py-2">
            <SourceMark>
              <YelpIcon />
            </SourceMark>{' '}
            Yelp
          </span>
          <span role="tab" aria-selected="false" className="flex items-center gap-1.5 px-1 py-2">
            <SourceMark>
              <BBBIcon />
            </SourceMark>{' '}
            BBB
          </span>
          <span role="tab" aria-selected="false" className="flex items-center gap-1.5 px-1 py-2">
            <SourceMark>
              <ExampleAIIcon />
            </SourceMark>{' '}
            AI Analysis
          </span>
        </div>

        <p className="mt-4 text-base font-bold text-slate-950">Past 180 Days</p>
        <ul
          aria-label="Example scorecard metrics"
          className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4"
        >
          <ExampleMetric label="Reviews" value="142" detail="24 / month" status="above" />
          <ExampleMetric label="Average Rating" value="4.21" status="average" trend />
          <ExampleMetric label="Your Response Rate" value="42%" status="below" />
          <ExampleMetric label="Avg Response Time" value="6.2 days" status="low" />
        </ul>
      </div>
    </section>
  );
}
